package com.alpha.showcase.common.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.text.font.FontFamily
import com.alpha.showcase.common.ui.settings.DarkThemePreference.Companion.FOLLOW_SYSTEM
import com.alpha.showcase.common.ui.settings.DarkThemePreference.Companion.ON
import com.alpha.showcase.common.ui.settings.SettingsViewModel

private val forestLightColorScheme = lightColorScheme(
    primary = Green40,
    onPrimary = Color.White,
    primaryContainer = Green90,
    onPrimaryContainer = Green10,
    inversePrimary = Green80,
    secondary = Teal40,
    onSecondary = Color.White,
    secondaryContainer = Teal90,
    onSecondaryContainer = Teal10,
    tertiary = Orange40,
    onTertiary = Color.White,
    tertiaryContainer = Orange90,
    onTertiaryContainer = Orange10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Grey99,
    onBackground = Grey10,
    surface = Grey99,
    onSurface = Grey10,
    inverseSurface = Grey20,
    inverseOnSurface = Grey95,
    surfaceVariant = BlueGrey90,
    onSurfaceVariant = BlueGrey30,
    outline = BlueGrey50,
    surfaceContainerLow = Grey99,
    surfaceContainer = Grey99,
)

private val forestDarkColorScheme = darkColorScheme(
    primary = Green80,
    onPrimary = Green20,
    primaryContainer = Green30,
    onPrimaryContainer = Green90,
    inversePrimary = Green40,
    secondary = Teal80,
    onSecondary = Teal20,
    secondaryContainer = Teal30,
    onSecondaryContainer = Teal90,
    tertiary = Orange80,
    onTertiary = Orange20,
    tertiaryContainer = Orange30,
    onTertiaryContainer = Orange90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Grey10,
    onBackground = Grey90,
    surface = Grey10,
    onSurface = Grey80,
    inverseSurface = Grey90,
    inverseOnSurface = Grey20,
    surfaceVariant = BlueGrey30,
    onSurfaceVariant = BlueGrey80,
    outline = BlueGrey60,
    surfaceContainerLowest = Grey10,
    surfaceContainerLow = Grey20,
    surfaceContainer = Grey20,
)

private val orchidLightColorScheme = lightColorScheme(
    primary = OrchidPurple40,
    onPrimary = Color.White,
    primaryContainer = OrchidPurple90,
    onPrimaryContainer = OrchidPurple10,
    inversePrimary = OrchidPurple80,
    secondary = OrchidPink40,
    onSecondary = Color.White,
    secondaryContainer = OrchidPink90,
    onSecondaryContainer = OrchidPink10,
    tertiary = OrchidCyan40,
    onTertiary = Color.White,
    tertiaryContainer = OrchidCyan90,
    onTertiaryContainer = OrchidCyan10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Grey99,
    onBackground = Grey10,
    surface = Grey99,
    onSurface = Grey10,
    inverseSurface = Grey20,
    inverseOnSurface = Grey95,
    surfaceVariant = BlueGrey90,
    onSurfaceVariant = BlueGrey30,
    outline = BlueGrey50,
    surfaceContainerLow = Grey99,
    surfaceContainer = Grey99,
)

private val orchidDarkColorScheme = darkColorScheme(
    primary = OrchidPurple80,
    onPrimary = OrchidPurple20,
    primaryContainer = OrchidPurple30,
    onPrimaryContainer = OrchidPurple90,
    inversePrimary = OrchidPurple40,
    secondary = OrchidPink80,
    onSecondary = OrchidPink20,
    secondaryContainer = OrchidPink30,
    onSecondaryContainer = OrchidPink90,
    tertiary = OrchidCyan80,
    onTertiary = OrchidCyan20,
    tertiaryContainer = OrchidCyan30,
    onTertiaryContainer = OrchidCyan90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Grey10,
    onBackground = Grey90,
    surface = Grey10,
    onSurface = Grey80,
    inverseSurface = Grey90,
    inverseOnSurface = Grey20,
    surfaceVariant = BlueGrey30,
    onSurfaceVariant = BlueGrey80,
    outline = BlueGrey60,
    surfaceContainerLowest = Grey10,
    surfaceContainerLow = Grey20,
    surfaceContainer = Grey20,
)

private val coastLightColorScheme = lightColorScheme(
    primary = Blue40,
    onPrimary = Color.White,
    primaryContainer = Blue90,
    onPrimaryContainer = Blue10,
    inversePrimary = Blue80,
    secondary = DarkBlue40,
    onSecondary = Color.White,
    secondaryContainer = DarkBlue90,
    onSecondaryContainer = DarkBlue10,
    tertiary = Yellow40,
    onTertiary = Color.White,
    tertiaryContainer = Yellow90,
    onTertiaryContainer = Yellow10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Grey95,
    onBackground = Grey10,
    surface = Grey95,
    onSurface = Grey10,
    surfaceContainer = Blue100,
    surfaceContainerLow = Blue100,
    surfaceContainerHigh = Grey99,
    surfaceContainerLowest = Grey95,
    surfaceContainerHighest = Color.White,
    surfaceVariant = BlueGrey90,
    onSurfaceVariant = BlueGrey30,
    outline = BlueGrey50,
    inverseSurface = Grey20,
    inverseOnSurface = Grey95,
)

private val coastDarkColorScheme = darkColorScheme(
    primary = Blue80,
    onPrimary = Blue20,
    primaryContainer = Blue30,
    onPrimaryContainer = Blue90,
    inversePrimary = Blue40,
    secondary = DarkBlue80,
    onSecondary = DarkBlue20,
    secondaryContainer = DarkBlue30,
    onSecondaryContainer = DarkBlue90,
    tertiary = Yellow80,
    onTertiary = Yellow20,
    tertiaryContainer = Yellow30,
    onTertiaryContainer = Yellow90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Grey10,
    onBackground = Grey90,
    surface = Grey10,
    onSurface = Grey80,
    inverseSurface = Grey90,
    inverseOnSurface = Grey20,
    surfaceVariant = BlueGrey30,
    onSurfaceVariant = BlueGrey80,
    outline = BlueGrey60,
    surfaceContainerLowest = Grey10,
    surfaceContainerLow = Grey20,
    surfaceContainer = Grey20,
)

private val sunsetLightColorScheme = lightColorScheme(
    primary = Sunset40,
    onPrimary = Color.White,
    primaryContainer = Sunset90,
    onPrimaryContainer = Sunset10,
    inversePrimary = Sunset80,
    secondary = Gold40,
    onSecondary = Color.White,
    secondaryContainer = Gold90,
    onSecondaryContainer = Gold10,
    tertiary = Berry40,
    onTertiary = Color.White,
    tertiaryContainer = Berry90,
    onTertiaryContainer = Berry10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Color(0xFFFFF8F4),
    onBackground = Grey10,
    surface = Color(0xFFFFF8F4),
    onSurface = Grey10,
    inverseSurface = Grey20,
    inverseOnSurface = Grey95,
    surfaceVariant = Color(0xFFF5DDD6),
    onSurfaceVariant = Berry30,
    outline = Color(0xFF9D857C),
    surfaceContainerLowest = Color(0xFFFFF8F4),
    surfaceContainerLow = Color(0xFFFFEFE8),
    surfaceContainer = Color(0xFFFFE7DE),
    surfaceContainerHigh = Color(0xFFFFDED3),
)

private val sunsetDarkColorScheme = darkColorScheme(
    primary = Sunset80,
    onPrimary = Sunset20,
    primaryContainer = Sunset30,
    onPrimaryContainer = Sunset90,
    inversePrimary = Sunset40,
    secondary = Gold80,
    onSecondary = Gold20,
    secondaryContainer = Gold30,
    onSecondaryContainer = Gold90,
    tertiary = Berry80,
    onTertiary = Berry20,
    tertiaryContainer = Berry30,
    onTertiaryContainer = Berry90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Color(0xFF1A120F),
    onBackground = Color(0xFFF3DFD7),
    surface = Color(0xFF1A120F),
    onSurface = Color(0xFFF3DFD7),
    inverseSurface = Grey90,
    inverseOnSurface = Grey20,
    surfaceVariant = Color(0xFF54433E),
    onSurfaceVariant = Color(0xFFD9C2BB),
    outline = Color(0xFFA58B82),
    surfaceContainerLowest = Color(0xFF120B08),
    surfaceContainerLow = Color(0xFF231916),
    surfaceContainer = Color(0xFF2C1F1A),
    surfaceContainerHigh = Color(0xFF352520),
)

private val noirLightColorScheme = lightColorScheme(
    primary = Noir40,
    onPrimary = Color.White,
    primaryContainer = Noir90,
    onPrimaryContainer = Noir10,
    inversePrimary = Noir80,
    secondary = Copper40,
    onSecondary = Color.White,
    secondaryContainer = Copper90,
    onSecondaryContainer = Copper10,
    tertiary = Stone40,
    onTertiary = Color.White,
    tertiaryContainer = Stone90,
    onTertiaryContainer = Stone10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Color(0xFFF7F2EE),
    onBackground = Noir10,
    surface = Color(0xFFF7F2EE),
    onSurface = Noir10,
    inverseSurface = Noir20,
    inverseOnSurface = Stone90,
    surfaceVariant = Color(0xFFE2D7D0),
    onSurfaceVariant = Stone30,
    outline = Color(0xFF8A7B74),
    surfaceContainerLowest = Color(0xFFF7F2EE),
    surfaceContainerLow = Color(0xFFF0E8E3),
    surfaceContainer = Color(0xFFE7DDD7),
    surfaceContainerHigh = Color(0xFFDDD2CB),
)

private val noirDarkColorScheme = darkColorScheme(
    primary = Noir80,
    onPrimary = Noir20,
    primaryContainer = Noir30,
    onPrimaryContainer = Noir90,
    inversePrimary = Noir40,
    secondary = Copper80,
    onSecondary = Copper20,
    secondaryContainer = Copper30,
    onSecondaryContainer = Copper90,
    tertiary = Stone80,
    onTertiary = Stone20,
    tertiaryContainer = Stone30,
    onTertiaryContainer = Stone90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Color(0xFF120F0E),
    onBackground = Color(0xFFECE0D9),
    surface = Color(0xFF120F0E),
    onSurface = Color(0xFFECE0D9),
    inverseSurface = Stone90,
    inverseOnSurface = Noir20,
    surfaceVariant = Color(0xFF4A403C),
    onSurfaceVariant = Color(0xFFD0C4BD),
    outline = Color(0xFF9C8E87),
    surfaceContainerLowest = Color(0xFF0A0707),
    surfaceContainerLow = Color(0xFF1A1615),
    surfaceContainer = Color(0xFF241F1E),
    surfaceContainerHigh = Color(0xFF2D2827),
)

private val oledLightColorScheme = lightColorScheme(
    primary = OledCyan40,
    onPrimary = Color.White,
    primaryContainer = OledCyan90,
    onPrimaryContainer = OledCyan10,
    inversePrimary = OledCyan80,
    secondary = OledMint40,
    onSecondary = Color.White,
    secondaryContainer = OledMint90,
    onSecondaryContainer = OledMint10,
    tertiary = OledAmber40,
    onTertiary = Color.White,
    tertiaryContainer = OledAmber90,
    onTertiaryContainer = OledAmber10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Color(0xFFF7FBFC),
    onBackground = Grey10,
    surface = Color(0xFFF7FBFC),
    onSurface = Grey10,
    inverseSurface = Grey20,
    inverseOnSurface = Grey95,
    surfaceVariant = Color(0xFFDCE5E8),
    onSurfaceVariant = Color(0xFF3F4A4F),
    outline = Color(0xFF6D7B81),
    surfaceContainerLowest = Color.White,
    surfaceContainerLow = Color(0xFFF1F6F8),
    surfaceContainer = Color(0xFFEAF1F4),
    surfaceContainerHigh = Color(0xFFE2EBEE),
)

private val oledDarkColorScheme = darkColorScheme(
    primary = OledCyan80,
    onPrimary = OledCyan20,
    primaryContainer = OledCyan30,
    onPrimaryContainer = OledCyan90,
    inversePrimary = OledCyan40,
    secondary = OledMint80,
    onSecondary = OledMint20,
    secondaryContainer = OledMint30,
    onSecondaryContainer = OledMint90,
    tertiary = OledAmber80,
    onTertiary = OledAmber20,
    tertiaryContainer = OledAmber30,
    onTertiaryContainer = OledAmber90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Color.Black,
    onBackground = Color(0xFFE6EDF2),
    surface = Color.Black,
    onSurface = Color(0xFFE6EDF2),
    inverseSurface = Color(0xFFE6EDF2),
    inverseOnSurface = Color.Black,
    surfaceVariant = Color(0xFF151B1E),
    onSurfaceVariant = Color(0xFFC2CBD0),
    outline = Color(0xFF3A454A),
    surfaceContainerLowest = Color.Black,
    surfaceContainerLow = Color(0xFF040608),
    surfaceContainer = Color(0xFF0A0D10),
    surfaceContainerHigh = Color(0xFF11161A),
)

private val auroraLightColorScheme = lightColorScheme(
    primary = Aurora40,
    onPrimary = Color.White,
    primaryContainer = Aurora90,
    onPrimaryContainer = Aurora10,
    inversePrimary = Aurora80,
    secondary = AuroraIndigo40,
    onSecondary = Color.White,
    secondaryContainer = AuroraIndigo90,
    onSecondaryContainer = AuroraIndigo10,
    tertiary = AuroraMint40,
    onTertiary = Color.White,
    tertiaryContainer = AuroraMint90,
    onTertiaryContainer = AuroraMint10,
    error = Red40,
    onError = Color.White,
    errorContainer = Red90,
    onErrorContainer = Red10,
    background = Color(0xFFF6F8FF),
    onBackground = Color(0xFF171C24),
    surface = Color(0xFFF6F8FF),
    onSurface = Color(0xFF171C24),
    inverseSurface = Color(0xFF2C313A),
    inverseOnSurface = Color(0xFFEFF0F7),
    surfaceVariant = Color(0xFFE0E5F1),
    onSurfaceVariant = Color(0xFF424755),
    outline = Color(0xFF727887),
    surfaceContainerLowest = Color.White,
    surfaceContainerLow = Color(0xFFF0F2FA),
    surfaceContainer = Color(0xFFE9ECF6),
    surfaceContainerHigh = Color(0xFFE3E7F2),
)

private val auroraDarkColorScheme = darkColorScheme(
    primary = Aurora80,
    onPrimary = Aurora20,
    primaryContainer = Aurora30,
    onPrimaryContainer = Aurora90,
    inversePrimary = Aurora40,
    secondary = AuroraIndigo80,
    onSecondary = AuroraIndigo20,
    secondaryContainer = AuroraIndigo30,
    onSecondaryContainer = AuroraIndigo90,
    tertiary = AuroraMint80,
    onTertiary = AuroraMint20,
    tertiaryContainer = AuroraMint30,
    onTertiaryContainer = AuroraMint90,
    error = Red80,
    onError = Red20,
    errorContainer = Red30,
    onErrorContainer = Red90,
    background = Color(0xFF0E1118),
    onBackground = Color(0xFFE1E3EC),
    surface = Color(0xFF0E1118),
    onSurface = Color(0xFFE1E3EC),
    inverseSurface = Color(0xFFE1E3EC),
    inverseOnSurface = Color(0xFF2B3039),
    surfaceVariant = Color(0xFF424755),
    onSurfaceVariant = Color(0xFFC2C7D5),
    outline = Color(0xFF8C92A1),
    surfaceContainerLowest = Color(0xFF080B12),
    surfaceContainerLow = Color(0xFF171B24),
    surfaceContainer = Color(0xFF1D212A),
    surfaceContainerHigh = Color(0xFF262A33),
)

private data class MonoInkTones(
    val exact: Color,
    val tone10: Color,
    val tone20: Color,
    val tone30: Color,
    val tone80: Color,
    val tone90: Color,
)

private val cobaltInkTones = MonoInkTones(
    exact = InkCobalt,
    tone10 = InkCobalt10,
    tone20 = InkCobalt20,
    tone30 = InkCobalt30,
    tone80 = InkCobalt80,
    tone90 = InkCobalt90,
)

private val terracottaInkTones = MonoInkTones(
    exact = InkTerracotta,
    tone10 = InkTerracotta10,
    tone20 = InkTerracotta20,
    tone30 = InkTerracotta30,
    tone80 = InkTerracotta80,
    tone90 = InkTerracotta90,
)

private val botanicalGreenInkTones = MonoInkTones(
    exact = InkBotanicalGreen,
    tone10 = InkBotanicalGreen10,
    tone20 = InkBotanicalGreen20,
    tone30 = InkBotanicalGreen30,
    tone80 = InkBotanicalGreen80,
    tone90 = InkBotanicalGreen90,
)

private val oxbloodInkTones = MonoInkTones(
    exact = InkOxblood,
    tone10 = InkOxblood10,
    tone20 = InkOxblood20,
    tone30 = InkOxblood30,
    tone80 = InkOxblood80,
    tone90 = InkOxblood90,
)

private val mintGreenInkTones = MonoInkTones(
    exact = InkMintGreen,
    tone10 = InkMintGreen10,
    tone20 = InkMintGreen20,
    tone30 = InkMintGreen30,
    tone80 = InkMintGreen80,
    tone90 = InkMintGreen90,
)

private val warmCharcoalInkTones = MonoInkTones(
    exact = InkWarmCharcoal,
    tone10 = InkWarmCharcoal10,
    tone20 = InkWarmCharcoal20,
    tone30 = InkWarmCharcoal30,
    tone80 = InkWarmCharcoal80,
    tone90 = InkWarmCharcoal90,
)

private val charcoalInkTones = MonoInkTones(
    exact = InkCharcoal,
    tone10 = InkCharcoal10,
    tone20 = InkCharcoal20,
    tone30 = InkCharcoal30,
    tone80 = InkCharcoal80,
    tone90 = InkCharcoal90,
)

private val signalRedInkTones = MonoInkTones(
    exact = InkSignalRed,
    tone10 = InkSignalRed10,
    tone20 = InkSignalRed20,
    tone30 = InkSignalRed30,
    tone80 = InkSignalRed80,
    tone90 = InkSignalRed90,
)

private fun monoLightColorScheme(
    dominant: MonoInkTones,
    accent: MonoInkTones,
    substrate: Color,
    onDominant: Color,
    onAccent: Color,
    onSurface: Color,
    primaryContainer: Color,
    secondaryContainer: Color,
    surfaceVariant: Color,
    onSurfaceVariant: Color,
    outline: Color,
    darkBase: Color,
) = lightColorScheme(
    primary = dominant.exact,
    onPrimary = onDominant,
    primaryContainer = primaryContainer,
    onPrimaryContainer = dominant.tone10,
    inversePrimary = dominant.tone80,
    secondary = accent.exact,
    onSecondary = onAccent,
    secondaryContainer = secondaryContainer,
    onSecondaryContainer = accent.tone10,
    tertiary = accent.exact,
    onTertiary = onAccent,
    tertiaryContainer = secondaryContainer,
    onTertiaryContainer = accent.tone10,
    error = accent.exact,
    onError = onAccent,
    errorContainer = secondaryContainer,
    onErrorContainer = accent.tone10,
    background = substrate,
    onBackground = onSurface,
    surface = substrate,
    onSurface = onSurface,
    surfaceVariant = surfaceVariant,
    onSurfaceVariant = onSurfaceVariant,
    surfaceTint = dominant.exact,
    inverseSurface = darkBase,
    inverseOnSurface = substrate,
    outline = outline,
    outlineVariant = lerp(substrate, outline, 0.45f),
    scrim = darkBase,
    surfaceBright = substrate,
    surfaceDim = lerp(substrate, dominant.exact, 0.12f),
    surfaceContainerLowest = substrate,
    surfaceContainerLow = lerp(substrate, dominant.exact, 0.025f),
    surfaceContainer = lerp(substrate, dominant.exact, 0.05f),
    surfaceContainerHigh = lerp(substrate, dominant.exact, 0.075f),
    surfaceContainerHighest = lerp(substrate, dominant.exact, 0.10f),
)

private fun monoDarkColorScheme(
    dominant: MonoInkTones,
    accent: MonoInkTones,
    substrate: Color,
    background: Color,
    onSurfaceVariant: Color,
    outline: Color,
) = darkColorScheme(
    primary = dominant.tone80,
    onPrimary = dominant.tone20,
    primaryContainer = dominant.tone30,
    onPrimaryContainer = dominant.tone90,
    inversePrimary = dominant.exact,
    secondary = accent.tone80,
    onSecondary = accent.tone20,
    secondaryContainer = accent.tone30,
    onSecondaryContainer = accent.tone90,
    tertiary = accent.tone80,
    onTertiary = accent.tone20,
    tertiaryContainer = accent.tone30,
    onTertiaryContainer = accent.tone90,
    error = accent.tone80,
    onError = accent.tone20,
    errorContainer = accent.tone30,
    onErrorContainer = accent.tone90,
    background = background,
    onBackground = substrate,
    surface = background,
    onSurface = substrate,
    surfaceVariant = lerp(background, dominant.tone80, 0.18f),
    onSurfaceVariant = onSurfaceVariant,
    surfaceTint = dominant.tone80,
    inverseSurface = substrate,
    inverseOnSurface = background,
    outline = outline,
    outlineVariant = lerp(background, outline, 0.55f),
    scrim = background,
    surfaceBright = lerp(background, substrate, 0.18f),
    surfaceDim = background,
    surfaceContainerLowest = background,
    surfaceContainerLow = lerp(background, substrate, 0.03f),
    surfaceContainer = lerp(background, substrate, 0.06f),
    surfaceContainerHigh = lerp(background, substrate, 0.09f),
    surfaceContainerHighest = lerp(background, substrate, 0.12f),
)

private val atlasLightColorScheme = monoLightColorScheme(
    dominant = cobaltInkTones,
    accent = terracottaInkTones,
    substrate = MonoNeutralWhite,
    onDominant = MonoNeutralWhite,
    onAccent = Color(0xFF1A0D07),
    onSurface = InkCobalt10,
    primaryContainer = Color(0xFFD9DFEE),
    secondaryContainer = Color(0xFFF2E3DA),
    surfaceVariant = Color(0xFFD9DFEE),
    onSurfaceVariant = InkCobalt20,
    outline = Color(0xFF758DD0),
    darkBase = Color(0xFF1A1B28),
)

private val atlasDarkColorScheme = monoDarkColorScheme(
    dominant = cobaltInkTones,
    accent = terracottaInkTones,
    substrate = MonoNeutralWhite,
    background = Color(0xFF1A1B28),
    onSurfaceVariant = Color(0xFFD3DAEC),
    outline = Color(0xFF73737A),
)

private val herbariumLightColorScheme = monoLightColorScheme(
    dominant = botanicalGreenInkTones,
    accent = oxbloodInkTones,
    substrate = MonoPaleBeige,
    onDominant = Color(0xFF000604),
    onAccent = MonoPaleBeige,
    onSurface = Color(0xFF1D0A0A),
    primaryContainer = Color(0xFFD0E2D0),
    secondaryContainer = Color(0xFFE6D5CD),
    surfaceVariant = Color(0xFFD0E2D0),
    onSurfaceVariant = InkBotanicalGreen20,
    outline = Color(0xFFB47976),
    darkBase = Color(0xFF001C0F),
)

private val herbariumDarkColorScheme = monoDarkColorScheme(
    dominant = botanicalGreenInkTones,
    accent = oxbloodInkTones,
    substrate = MonoPaleBeige,
    background = Color(0xFF001C0F),
    onSurfaceVariant = Color(0xFFC9DECC),
    outline = Color(0xFF657468),
)

private val journalLightColorScheme = monoLightColorScheme(
    dominant = mintGreenInkTones,
    accent = warmCharcoalInkTones,
    substrate = MonoCoolGray,
    onDominant = InkWarmCharcoal,
    onAccent = MonoCoolGray,
    onSurface = InkWarmCharcoal,
    primaryContainer = Color(0xFFD4E1D6),
    secondaryContainer = Color(0xFFCDCDCA),
    surfaceVariant = Color(0xFFD4E1D6),
    onSurfaceVariant = InkWarmCharcoal20,
    outline = Color(0xFF848381),
    darkBase = Color(0xFF122018),
)

private val journalDarkColorScheme = monoDarkColorScheme(
    dominant = mintGreenInkTones,
    accent = warmCharcoalInkTones,
    substrate = MonoCoolGray,
    background = Color(0xFF122018),
    onSurfaceVariant = Color(0xFFD0E0D3),
    outline = Color(0xFF6E766F),
)

private val signalLightColorScheme = monoLightColorScheme(
    dominant = charcoalInkTones,
    accent = signalRedInkTones,
    substrate = MonoCoolGray,
    onDominant = MonoCoolGray,
    onAccent = Color(0xFFFAECEC),
    onSurface = InkCharcoal,
    primaryContainer = Color(0xFFCDCECB),
    secondaryContainer = Color(0xFFE4CECA),
    surfaceVariant = Color(0xFFE4CECA),
    onSurfaceVariant = InkSignalRed20,
    outline = Color(0xFF818385),
    darkBase = InkCharcoal10,
)

private val signalDarkColorScheme = monoDarkColorScheme(
    dominant = charcoalInkTones,
    accent = signalRedInkTones,
    substrate = MonoCoolGray,
    background = InkCharcoal10,
    onSurfaceVariant = InkCharcoal90,
    outline = Color(0xFF6E7278),
)


private fun resolveColorScheme(
    themeStyle: AppThemeStyle,
    isDark: Boolean,
) = when (themeStyle) {
    AppThemeStyle.Coast -> if (isDark) coastDarkColorScheme else coastLightColorScheme
    AppThemeStyle.Forest -> if (isDark) forestDarkColorScheme else forestLightColorScheme
    AppThemeStyle.Sunset -> if (isDark) sunsetDarkColorScheme else sunsetLightColorScheme
    AppThemeStyle.Orchid -> if (isDark) orchidDarkColorScheme else orchidLightColorScheme
    AppThemeStyle.Noir -> if (isDark) noirDarkColorScheme else noirLightColorScheme
    AppThemeStyle.Oled -> if (isDark) oledDarkColorScheme else oledLightColorScheme
    AppThemeStyle.Aurora -> if (isDark) auroraDarkColorScheme else auroraLightColorScheme
    AppThemeStyle.Atlas -> if (isDark) atlasDarkColorScheme else atlasLightColorScheme
    AppThemeStyle.Herbarium -> if (isDark) herbariumDarkColorScheme else herbariumLightColorScheme
    AppThemeStyle.Journal -> if (isDark) journalDarkColorScheme else journalLightColorScheme
    AppThemeStyle.Signal -> if (isDark) signalDarkColorScheme else signalLightColorScheme
}

fun resolveThemeIsDark(
    darkMode: Int,
    systemIsDark: Boolean,
): Boolean = if (darkMode == FOLLOW_SYSTEM) systemIsDark else darkMode == ON

fun resolveThemeBackground(
    themeStyle: AppThemeStyle,
    isDark: Boolean,
): Color = resolveColorScheme(themeStyle, isDark).background

internal val LocalThemeIsDark = compositionLocalOf { mutableStateOf(true) }

@Composable
internal fun AppTheme(
    fontFamily: FontFamily = FontFamily.Default,
    content: @Composable () -> Unit
) {
    val systemIsDark = isSystemInDarkTheme()
    val darkMode = SettingsViewModel.darkModeFlow.collectAsState().value
    val themeStyle = SettingsViewModel.themeStyleFlow.collectAsState().value
    val isDark = resolveThemeIsDark(darkMode, systemIsDark)

    val isDarkState = remember { mutableStateOf(isDark) }
    isDarkState.value = isDark

    CompositionLocalProvider(
        LocalThemeIsDark provides isDarkState
    ) {
        SystemAppearance(isDark)

        MaterialTheme(
            colorScheme = resolveColorScheme(themeStyle, isDark),
            typography = materialTypography(fontFamily),
            content = content,
        )
    }
}

@Composable
internal expect fun SystemAppearance(isDark: Boolean)
